import { Component, AfterViewInit, OnDestroy } from '@angular/core';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap?.registerPlugin(ScrollTrigger);

@Component({
  selector: 'app-my-page-animate',
  templateUrl: './my-page-animate.component.html',
  styleUrls: ['./my-page-animate.component.scss'],
})
export class MyPageAnimateComponent implements AfterViewInit, OnDestroy {
  private animations: gsap.core.Tween[] = [];
  private scrollTriggers: ScrollTrigger[] = [];

  ngAfterViewInit(): void {
    this.initializeAnimations();
  }

  ngOnDestroy(): void {
    // Clean up animations
    this.animations.forEach(animation => animation.kill());
    this.scrollTriggers.forEach(trigger => trigger.kill());
    ScrollTrigger.getAll().forEach(trigger => trigger.kill());
  }

  private initializeAnimations(): void {
    // 1. Enhanced card flip animation with 3D depth
    const cardAnimation = gsap.to('.voucher-card', {
      rotationY: 180,
      rotationX: 5,
      scale: 1.05,
      z: 50,
      transformStyle: 'preserve-3d',
      ease: 'power2.inOut',
      scrollTrigger: {
        trigger: '.voucher-container',
        start: 'top 70%',
        end: 'bottom 30%',
        scrub: 1.5,
        markers: false, // Remove in production
      },
    });

    // 2. SVG path drawing animation
    const svgAnimation = gsap.fromTo('.voucher-card path', 
      {
        strokeDasharray: 1000,
        strokeDashoffset: 1000,
      },
      {
        strokeDashoffset: 0,
        duration: 2,
        stagger: {
          amount: 1.5,
          from: 'random'
        },
        ease: 'power2.inOut',
        scrollTrigger: {
          trigger: '.voucher-container',
          start: 'top 80%',
          end: 'bottom 20%',
          scrub: 1,
        },
      }
    );

    // 3. Improved typewriter effect
    const typewriterAnimation = gsap.to('.typed-text', {
      duration: 3,
      text: {
        value: "Let's explore the world of vouchers!",
        delimiter: '',
      },
      ease: 'power2.inOut',
      scrollTrigger: {
        trigger: '.subtitle-text',
        start: 'top 85%',
        end: 'bottom 15%',
        toggleActions: 'play pause resume reset',
      },
    });

    // 4. Enhanced title animation sequence
    const titleAnimation = gsap.timeline({
      scrollTrigger: {
        trigger: '.voucher-container',
        start: 'top 90%',
        end: 'bottom 10%',
        toggleActions: 'play none none reverse',
      },
    });

    titleAnimation
      .from('.title-text', {
        duration: 1.2,
        y: 120,
        opacity: 0,
        rotationX: 85,
        transformOrigin: 'bottom center',
        ease: 'power3.out',
        stagger: 0.1,
      })
      .from('.title-decoration', {
        duration: 1.5,
        scale: 0,
        rotation: 180,
        opacity: 0,
        ease: 'back.out(1.8)',
      }, '-=0.8')
      .from('.head_test svg', {
        duration: 1,
        scale: 0.8,
        opacity: 0,
        rotation: -10,
        ease: 'elastic.out(1, 0.5)',
      }, '-=0.5');

    // 5. Improved stagger text animation
    const textStaggerAnimation = gsap.from('.text-part', {
      duration: 1.2,
      y: 60,
      opacity: 0,
      rotationX: 45,
      stagger: {
        amount: 0.6,
        from: 'center'
      },
      ease: 'back.out(1.2)',
      scrollTrigger: {
        trigger: '.description-text',
        start: 'top 75%',
        end: 'bottom 25%',
        toggleActions: 'play none none reverse',
      },
    });

    // 6. Enhanced decoration line animation
    const lineAnimation = gsap.from('.decoration-line', {
      duration: 2,
      scaleX: 0,
      transformOrigin: 'left center',
      ease: 'expo.out',
      scrollTrigger: {
        trigger: '.decoration-line',
        start: 'top 85%',
        end: 'bottom 15%',
        toggleActions: 'play none none reverse',
      },
    });

    // 7. Improved floating animation
    const floatingAnimation = gsap.to('.voucher-icon', {
      duration: 4,
      y: -25,
      rotation: 3,
      ease: 'sine.inOut',
      repeat: -1,
      yoyo: true,
    });

    // 8. Enhanced hover effects
    this.initializeHoverEffects();

    // 9. Parallax effect for section divider
    const parallaxAnimation = gsap.to('.section-divider', {
      yPercent: -20,
      ease: 'none',
      scrollTrigger: {
        trigger: '.section-divider',
        start: 'top bottom',
        end: 'bottom top',
        scrub: 1,
      },
    });

    // 10. Background pattern animation
    // const patternAnimation = gsap.to('.voucher-card pattern image', {
    //   duration: 20,
    //   rotation: 360,
    //   transformOrigin: 'center center',
    //   repeat: -1,
    //   ease: 'none',
    // });

    // Store animations for cleanup
    this.animations.push(
      cardAnimation,
      svgAnimation,
      typewriterAnimation,
      textStaggerAnimation,
      lineAnimation,
      floatingAnimation,
      parallaxAnimation,
      // patternAnimation
    );

    // Store scroll triggers
    this.scrollTriggers = ScrollTrigger.getAll();
  }

  private initializeHoverEffects(): void {
    const textElements = document.querySelectorAll('.title-text, .subtitle-text');
    
    textElements.forEach((text) => {
      // Mouse enter animation
      const enterAnimation = gsap.to(text, {
        duration: 0.4,
        scale: 1.08,
        y: -5,
        color: 'var(--accent)',
        textShadow: '0 0 20px rgba(139, 92, 246, 0.6)',
        ease: 'power2.out',
        paused: true,
      });

      // Mouse leave animation
      const leaveAnimation = gsap.to(text, {
        duration: 0.4,
        scale: 1,
        y: 0,
        color: 'inherit',
        textShadow: 'none',
        ease: 'power2.out',
        paused: true,
      });

      text.addEventListener('mouseenter', () => {
        leaveAnimation.pause();
        enterAnimation.play();
      });

      text.addEventListener('mouseleave', () => {
        enterAnimation.pause();
        leaveAnimation.play();
      });

      this.animations.push(enterAnimation, leaveAnimation);
    });

    // Card hover effect
    const card = document.querySelector('.voucher-card');
    if (card) {
      const cardHover = gsap.to(card, {
        duration: 0.3,
        scale: 1.02,
        y: -10,
        ease: 'power2.out',
        paused: true,
      });

      card.addEventListener('mouseenter', () => cardHover.play());
      card.addEventListener('mouseleave', () => cardHover.reverse());
      
      this.animations.push(cardHover);
    }
  }
}